/**
 * paths.ts — BETA 23.24 · DIRT TRAILS TO PARKS (Dylan's correction of 23.23)
 *
 * "The problem with the roads is they have no reason... the roads are
 *  supposed to be leading to, like, parks or something. It needs to be more
 *  like a dirt path. Light poles look good. And no random sprites on top of
 *  the road."
 *
 * Design v2:
 *  - PARKS are the destinations: seeded pocket-plazas scattered sparsely
 *    across the endless lot (one every few thousand wu). Each park is a
 *    worn dirt clearing furnished with its own seeded arrangement (benches,
 *    dead trees, fire barrel, sometimes a Surge Statue centerpiece).
 *  - TRAILS connect park-to-park (nearest neighbors) — every path leads to
 *    a place, not to a vending machine.
 *  - The surface is a DIRT PATH now, not a concrete road: narrower, warm
 *    earth tones, ragged edges, only a soft NE shading (dirt isn't a slab).
 *  - Lamps still line the trails (Dylan: "light poles look good").
 *  - worldProps keeps every scatter prop off the trail AND out of the plaza
 *    (parks furnish themselves); mapChunks keeps decals off both too.
 */

import { CHUNK_WU, cellRng, getGroundPois } from './mapChunks';
import { forestPocketsInRange } from './forest';   // 23.57 places (runtime-only — cycle-safe)
import { biomeLampSpacingMulAt, biomeAt } from './biomes';   // G2 lamps · G3 N1 region-aware places — pure fns, cycle-safe

const PATH_WIDTH_WU = 118;   // 23.43: +45% again (Dylan vs the mockups: "the roads are so skinny")
const SEG_STEP_WU = 56;
const PARK_CELL_WU = 6144;      // one candidate park per cell (thinned below)
const PARK_CHANCE = 0.62;
const PARK_MIN_DIST = 3800;
const PARK_LINK_MAX = 9000;
const LINKS_PER_PARK = 2;
const LAMP_SPACING_WU = 620;
const LAMP_SIDE_WU = 58;
export const PARK_PLAZA_R = 330;   // dirt clearing radius (wu)

interface Seg { x1: number; y1: number; x2: number; y2: number }
export interface PathLamp { x: number; y: number; flip: boolean }
export type SetPieceKind = 'park' | 'graveyard' | 'town_square';
export interface ParkSite { x: number; y: number; kind: SetPieceKind }
export interface ParkProp { defId: string; x: number; y: number; flip?: boolean }

let _builtSeed: number | null = null;
let _parks: ParkSite[] = [];
// 23.56 fix-pack — anchor points (spawn/portal/extraction) that MUST get a
// spur trail to the network. Registered by the engine at mapgen; the portal
// used to get a carved clearing but NO connecting trail — a walled-off
// descend portal (caught by __validateWorld).
let _spurAnchors: Array<{ x: number; y: number }> = [];
// 23.57 — PLACES: every forest pocket is a purposeful node (Dylan's
// intentional-world pack: "build places... fix 'why am I walking here?'")
export type PlaceKind = 'camp' | 'junkyard' | 'crash_site' | 'grave_patch' | 'nest' | 'arena' | 'checkpoint' | 'neighborhood';
/** 23.75 N1-B — a neighborhood lot: a house footprint anchor + the compass
 *  angle its FRONT faces (toward the street). House ART lands at N2 wiring once
 *  the direction-law backs are approved; until then `lots` drives the [t] dev
 *  overlay so Dylan can gate the street SHAPE with zero art spend. */
export interface Lot { x: number; y: number; hw: number; hh: number; face: number }
export interface Place { x: number; y: number; rx: number; ry: number; kind: PlaceKind; groundKit: GroundKitId; lots?: Lot[] }
// 23.58 — pack Phase 1: every location kind owns a GROUND MATERIAL KIT
// ("if the module changes, the ground should change") — mapChunks bakes it.
export type GroundKitId = 'junkyard_floor' | 'camp_ground' | 'grave_soil' | 'scorched' | 'blackened' | 'compacted' | 'yard';
const KIND_GROUND: Record<PlaceKind, GroundKitId> = {
  junkyard: 'junkyard_floor', camp: 'camp_ground', grave_patch: 'grave_soil',
  crash_site: 'scorched', nest: 'blackened', arena: 'compacted', checkpoint: 'compacted',
  neighborhood: 'yard',
};
let _places: Place[] = [];
// 23.61 — GROUND PASS 2: every machine's station KIND is recorded here so the
// prop dressing (below) and the coded ground pad (groundKits.paintStationPad)
// derive from ONE decision — placement and ground-bake can never disagree.
export type StationKind = 'roadside' | 'booth' | 'kiosk';
export interface MachineStation { x: number; y: number; kind: StationKind }
let _stations: MachineStation[] = [];
let _segs: Seg[] = [];
let _lamps: PathLamp[] = [];
let _parkProps: ParkProp[] = [];
const _buckets = new Map<string, Seg[]>();

function _h2(a: number, b: number): number {
  let h = (Math.imul(Math.round(a), 0x9E3779B1) ^ Math.imul(Math.round(b), 0x85EBCA6B)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xC2B2AE35) >>> 0;
  return h;
}

// 23.43 — ROAD WOBBLE (Dylan: "not natural, perfectly straight"): every
// flattened trail point drifts perpendicular on two detuned sine waves, so
// long runs meander like walked-in dirt instead of drafting-table lines.
// Applied at GENERATION, so bake + clearance + carve + lamps all agree.
function _wobble(t: number, len: number, phase: number): number {
  return (Math.sin(t * len / 240 * Math.PI * 2 + phase) * 0.62
        + Math.sin(t * len / 97 * Math.PI * 2 + phase * 1.7) * 0.38) * 30;
}

// Park sites live on a a fixed seeded lattice around the origin region.
// (The playable POI region is ~0..10000; we generate parks over a wide halo
// so trails exist wherever the player roams in the endless lot.)
const PARK_RANGE_CELLS = 6;   // ±6 cells ≈ ±37k wu — far beyond normal runs

function _ensureNetwork(seed: number): void {
  if (_builtSeed === seed) return;
  _builtSeed = seed;
  try {
    _buildNetwork(seed);
  } catch (err) {
    // 23.47 — a silent throw here used to leave a PARTIAL network cached
    // forever (world renders with no trails and nobody knows why). Scream.
    try { console.error('[paths] NETWORK BUILD FAILED — world will have no trails!', err); } catch { /* noop */ }
  }
}

function _buildNetwork(seed: number): void {
  _parks = [];
  _places = [];   // 23.57
  _stations = [];   // 23.61
  _segs = [];
  _lamps = [];
  _parkProps = [];
  _buckets.clear();

  // 1. candidate parks per lattice cell, deterministic thinning by hash
  const cands: Array<ParkSite & { pri: number }> = [];
  for (let cy = -PARK_RANGE_CELLS; cy <= PARK_RANGE_CELLS; cy++) {
    for (let cx = -PARK_RANGE_CELLS; cx <= PARK_RANGE_CELLS; cx++) {
      const rng = cellRng(cx, cy, seed, 909);
      if (rng() > PARK_CHANCE) continue;
      const x = (cx + 0.18 + rng() * 0.64) * PARK_CELL_WU;
      const y = (cy + 0.18 + rng() * 0.64) * PARK_CELL_WU;
      // 23.34 — SET-PIECE KINDS: parks, graveyards, ruined town squares
      const kroll = rng();
      const kind = kroll < 0.55 ? 'park' as const : kroll < 0.8 ? 'graveyard' as const : 'town_square' as const;
      cands.push({ x, y, kind, pri: _h2(x, y) });
    }
  }
  cands.sort((a, b) => a.pri - b.pri);
  for (const c of cands) {
    let ok = true;
    for (const p of _parks) {
      if (Math.hypot(c.x - p.x, c.y - p.y) < PARK_MIN_DIST) { ok = false; break; }
    }
    if (ok) _parks.push({ x: c.x, y: c.y, kind: c.kind });
  }

  // 2. trails: each park links to its nearest neighbors (deduped)
  const edges = new Set<string>();
  for (let i = 0; i < _parks.length; i++) {
    const ds: Array<{ j: number; d: number }> = [];
    for (let j = 0; j < _parks.length; j++) {
      if (j === i) continue;
      const d = Math.hypot(_parks[i].x - _parks[j].x, _parks[i].y - _parks[j].y);
      if (d <= PARK_LINK_MAX) ds.push({ j, d });
    }
    ds.sort((a, b) => a.d - b.d);
    for (const { j } of ds.slice(0, LINKS_PER_PARK)) edges.add(i < j ? `${i},${j}` : `${j},${i}`);
  }

  // 2a. 23.56 fix-pack (docs 07/08, caught by __validateWorld): the nearest-
  // neighbor graph can FRAGMENT — machines/portal on a minor component were
  // unreachable by walking (forest walls everywhere else). Bridge components
  // with the closest cross-component park pair until the network is ONE piece.
  {
    const parent = _parks.map((_, i) => i);
    const find = (a: number): number => { while (parent[a] !== a) { parent[a] = parent[parent[a]]; a = parent[a]; } return a; };
    for (const e of Array.from(edges)) { const [i, j] = e.split(',').map(Number); parent[find(i)] = find(j); }
    for (let guard = 0; guard < _parks.length && _parks.length > 1; guard++) {
      const main = find(0);
      let allOne = true;
      let bi = -1, bj = -1, bd = Infinity;
      for (let i = 0; i < _parks.length; i++) {
        if (find(i) !== main) { allOne = false; continue; }
        for (let j = 0; j < _parks.length; j++) {
          if (find(j) === main) continue;
          const d = Math.hypot(_parks[i].x - _parks[j].x, _parks[i].y - _parks[j].y);
          if (d < bd) { bd = d; bi = i; bj = j; }
        }
      }
      if (allOne || bi < 0) break;
      edges.add(bi < bj ? `${bi},${bj}` : `${bj},${bi}`);
      parent[find(bi)] = find(bj);
    }
  }

  for (const e of Array.from(edges)) {
    const [i, j] = e.split(',').map(Number);
    const ax = _parks[i].x, ay = _parks[i].y, bx = _parks[j].x, by = _parks[j].y;
    const rng = cellRng(_h2(ax, ay) & 0xffff, _h2(bx, by) & 0xffff, seed, 31);
    const mx = (ax + bx) / 2, my = (ay + by) / 2;
    const dx = bx - ax, dy = by - ay;
    const len = Math.hypot(dx, dy) || 1;
    const bow = (rng() - 0.5) * Math.min(900, len * 0.35);
    const cxp = mx + (-dy / len) * bow, cyp = my + (dx / len) * bow;
    const steps = Math.max(4, Math.ceil(len / SEG_STEP_WU));
    let px = ax, py = ay, walked = 0, nextLamp = LAMP_SPACING_WU * (0.5 + rng() * 0.5);
    let lampSide = rng() < 0.5 ? 1 : -1;
    const wPhase = rng() * Math.PI * 2;
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      let qx = (1 - t) * (1 - t) * ax + 2 * (1 - t) * t * cxp + t * t * bx;
      let qy = (1 - t) * (1 - t) * ay + 2 * (1 - t) * t * cyp + t * t * by;
      // 23.43 — meander (fades at the endpoints so junctions stay put)
      const wob = _wobble(t, len, wPhase) * Math.min(1, Math.min(t, 1 - t) * 6);
      qx += (-dy / len) * wob;
      qy += (dx / len) * wob;
      const seg = { x1: px, y1: py, x2: qx, y2: qy };
      _segs.push(seg);
      const minCx = Math.floor((Math.min(px, qx) - 300) / CHUNK_WU);
      const maxCx = Math.floor((Math.max(px, qx) + 300) / CHUNK_WU);
      const minCy = Math.floor((Math.min(py, qy) - 300) / CHUNK_WU);
      const maxCy = Math.floor((Math.max(py, qy) + 300) / CHUNK_WU);
      for (let byy = minCy; byy <= maxCy; byy++)
        for (let bxx = minCx; bxx <= maxCx; bxx++) {
          const bk = `${bxx},${byy}`;
          let arr = _buckets.get(bk);
          if (!arr) { arr = []; _buckets.set(bk, arr); }
          arr.push(seg);
        }
      const dlen = Math.hypot(qx - px, qy - py);
      walked += dlen;
      if (walked >= nextLamp && t > 0.1 && t < 0.9) {
        const ux = (qx - px) / (dlen || 1), uy = (qy - py) / (dlen || 1);
        // 23.32 - dome hangs AWAY from the road (art dome points RIGHT of
        // the pole -> flip when the away-direction points left)
        const awayX = (-uy) * lampSide;
        // 23.39 — Dylan: "all the lamps are backward" — the dome hangs
        // TOWARD the road now (light the trail, not the dirt behind it).
        // Art default = dome extends RIGHT; flip when away points right
        // so the dome swings left, back over the road.
        _lamps.push({ x: qx + (-uy) * LAMP_SIDE_WU * lampSide, y: qy + ux * LAMP_SIDE_WU * lampSide, flip: awayX > 0 });
        lampSide = -lampSide;
        // G2 — biome lamp dial: the NEXT interval scales with the biome at
        // this lamp (forest = sparser). Scaling the increment, never the
        // accumulated threshold — the threshold carries every prior interval.
        nextLamp += LAMP_SPACING_WU * biomeLampSpacingMulAt(qx, qy, seed);
      }
      px = qx; py = qy;
    }
  }

  // 2b. 23.41 — POI SPURS (G1 forest walls): every machine/shrine gets a
  // short dirt spur to the nearest trail, so when the forest closes in
  // nothing becomes an unreachable island. Spurs join _segs/_buckets, so
  // they bake as dirt, carve forest corridor, and count for clearances —
  // all for free. No lamps on spurs (they're driveways, not streets).
  const _pushSeg = (x1: number, y1: number, x2: number, y2: number) => {
    const seg = { x1, y1, x2, y2 };
    _segs.push(seg);
    const minCx = Math.floor((Math.min(x1, x2) - 300) / CHUNK_WU);
    const maxCx = Math.floor((Math.max(x1, x2) + 300) / CHUNK_WU);
    const minCy = Math.floor((Math.min(y1, y2) - 300) / CHUNK_WU);
    const maxCy = Math.floor((Math.max(y1, y2) + 300) / CHUNK_WU);
    for (let byy = minCy; byy <= maxCy; byy++)
      for (let bxx = minCx; bxx <= maxCx; bxx++) {
        const bk = `${bxx},${byy}`;
        let arr = _buckets.get(bk);
        if (!arr) { arr = []; _buckets.set(bk, arr); }
        arr.push(seg);
      }
  };
  const _spurSources = [...getGroundPois().map(p => ({ x: p.x, y: p.y })), ..._spurAnchors];
  for (const poi of _spurSources) {
    let bd = Infinity, bx2 = 0, by2 = 0;
    for (const sg of _segs) {
      const vx = sg.x2 - sg.x1, vy = sg.y2 - sg.y1;
      const ll = vx * vx + vy * vy || 1;
      let t = ((poi.x - sg.x1) * vx + (poi.y - sg.y1) * vy) / ll;
      t = Math.max(0, Math.min(1, t));
      const qx = sg.x1 + vx * t, qy = sg.y1 + vy * t;
      const d = Math.hypot(poi.x - qx, poi.y - qy);
      if (d < bd) { bd = d; bx2 = qx; by2 = qy; }
    }
    // 23.56 fix-pack — the old 3400wu remoteness cap left far machines as
    // unreachable clearing-bubbles ("if it exists, you can walk to it").
    if (!isFinite(bd) || bd < 130) continue;   // on a trail already
    const rngS = cellRng(Math.round(poi.x / 32), Math.round(poi.y / 32), seed, 515);
    const mx = (poi.x + bx2) / 2, my = (poi.y + by2) / 2;
    const dxs = bx2 - poi.x, dys = by2 - poi.y;
    const ls = Math.hypot(dxs, dys) || 1;
    const jog = (rngS() - 0.5) * Math.min(140, ls * 0.3);
    const jx = mx + (-dys / ls) * jog, jy = my + (dxs / ls) * jog;
    const steps = Math.max(2, Math.ceil(ls / SEG_STEP_WU));
    let px2 = poi.x, py2 = poi.y;
    const sPhase = rngS() * Math.PI * 2;
    for (let st = 1; st <= steps; st++) {
      const t = st / steps;
      let qx = (1 - t) * (1 - t) * poi.x + 2 * (1 - t) * t * jx + t * t * bx2;
      let qy = (1 - t) * (1 - t) * poi.y + 2 * (1 - t) * t * jy + t * t * by2;
      const wob = _wobble(t, ls, sPhase) * 0.6 * Math.min(1, Math.min(t, 1 - t) * 6);
      qx += (-dys / ls) * wob;
      qy += (dxs / ls) * wob;
      _pushSeg(px2, py2, qx, qy);
      px2 = qx; py2 = qy;
    }
  }

  // 2c. 23.57 — PLACES (Dylan's intentional-world pack: "stop making empty
  // dirt pockets... build places"). Every forest pocket becomes a NODE with
  // a PURPOSE: it gets a spur trail into it (Dylan: "there should be trails
  // going into those") and a seeded set-piece built from the prop library.
  // Pocket geometry comes from forest.ts's shared generator, so the clearing
  // and its dressing can never disagree.
  {
    const R = PARK_RANGE_CELLS * PARK_CELL_WU;
    for (const pk of forestPocketsInRange(-R, -R, R, R, seed)) {
      // pockets that already merge into a park plaza stay park territory
      let nearPark = false;
      for (const p of _parks) {
        if (Math.hypot(pk.x - p.x, pk.y - p.y) < PARK_PLAZA_R + pk.rx * 0.6) { nearPark = true; break; }
      }
      if (nearPark) continue;
      const prng = cellRng(Math.round(pk.x / 128), Math.round(pk.y / 128), seed, 9393);
      // KIND by size + roll (pack doc 08 buckets: majors need big rooms).
      // G3 N1 — REGION-AWARE: inside the neighborhood biome the rolls flip
      // suburban (streets of yards — big pockets are almost always yards;
      // small ones read as checkpoints/junk, never nests or grave patches).
      const roll = prng();
      const inHood = biomeAt(pk.x, pk.y, seed) === 'neighborhood';
      const kind: PlaceKind = pk.rx > 1350
        ? (inHood
          ? (roll < 0.72 ? 'neighborhood' : roll < 0.88 ? 'junkyard' : 'crash_site')
          : (roll < 0.30 ? 'neighborhood' : roll < 0.52 ? 'crash_site' : roll < 0.72 ? 'junkyard' : roll < 0.88 ? 'arena' : 'grave_patch'))
        : (inHood
          ? (roll < 0.40 ? 'checkpoint' : roll < 0.70 ? 'junkyard' : roll < 0.86 ? 'camp' : 'arena')
          : (roll < 0.26 ? 'camp' : roll < 0.44 ? 'grave_patch' : roll < 0.60 ? 'nest' : roll < 0.76 ? 'arena' : roll < 0.88 ? 'checkpoint' : 'junkyard'));
      _places.push({ x: pk.x, y: pk.y, rx: pk.rx, ry: pk.ry, kind, groundKit: KIND_GROUND[kind] });

      // spur trail INTO the pocket (nearest segment; uncapped — reachable law)
      let bd = Infinity, bx2 = 0, by2 = 0;
      for (const sg of _segs) {
        const vx = sg.x2 - sg.x1, vy = sg.y2 - sg.y1;
        const ll = vx * vx + vy * vy || 1;
        let t = ((pk.x - sg.x1) * vx + (pk.y - sg.y1) * vy) / ll;
        t = Math.max(0, Math.min(1, t));
        const qx = sg.x1 + vx * t, qy = sg.y1 + vy * t;
        const d = Math.hypot(pk.x - qx, pk.y - qy);
        if (d < bd) { bd = d; bx2 = qx; by2 = qy; }
      }
      if (isFinite(bd) && bd >= 130) {
        const dxs = bx2 - pk.x, dys = by2 - pk.y;
        const ls = Math.hypot(dxs, dys) || 1;
        const jog = (prng() - 0.5) * Math.min(160, ls * 0.3);
        const jx = (pk.x + bx2) / 2 + (-dys / ls) * jog, jy = (pk.y + by2) / 2 + (dxs / ls) * jog;
        const steps = Math.max(2, Math.ceil(ls / SEG_STEP_WU));
        let px2 = pk.x, py2 = pk.y;
        const sPhase = prng() * Math.PI * 2;
        for (let st = 1; st <= steps; st++) {
          const t = st / steps;
          let qx = (1 - t) * (1 - t) * pk.x + 2 * (1 - t) * t * jx + t * t * bx2;
          let qy = (1 - t) * (1 - t) * pk.y + 2 * (1 - t) * t * jy + t * t * by2;
          const wob = _wobble(t, ls, sPhase) * 0.6 * Math.min(1, Math.min(t, 1 - t) * 6);
          qx += (-dys / ls) * wob;
          qy += (dxs / ls) * wob;
          _pushSeg(px2, py2, qx, qy);
          px2 = qx; py2 = qy;
        }
      }

      // FURNISH by kind (pack docs 03/05/06: one focal point + secondaries;
      // rides the same forced-placement pipeline as park furniture)
      const P = (defId: string, fx: number, fy: number, flip?: boolean) =>
        _parkProps.push({ defId, x: pk.x + fx * pk.rx, y: pk.y + fy * pk.ry, flip });
      const jit = () => (prng() - 0.5) * 0.14;
      // 23.68 §3b C1 — build-time path distance over the LIVE _segs array
      // (distToNearestPath would re-enter _ensureNetwork mid-build). The spur
      // through this place is already pushed, so tents can see it.
      const _segDistLive = (x: number, y: number, maxWu = 300): number => {
        let best = maxWu * maxWu;
        for (const s of _segs) {
          if (Math.min(s.x1, s.x2) > x + maxWu || Math.max(s.x1, s.x2) < x - maxWu ||
              Math.min(s.y1, s.y2) > y + maxWu || Math.max(s.y1, s.y2) < y - maxWu) continue;
          const vx = s.x2 - s.x1, vy = s.y2 - s.y1;
          const ll = vx * vx + vy * vy || 1;
          let t = ((x - s.x1) * vx + (y - s.y1) * vy) / ll;
          t = t < 0 ? 0 : t > 1 ? 1 : t;
          const dx = x - (s.x1 + vx * t), dy = y - (s.y1 + vy * t);
          const d = dx * dx + dy * dy;
          if (d < best) best = d;
        }
        return Math.sqrt(best);
      };
      if (kind === 'camp') {
        P('campfire_ring', jit(), jit());
        // 23.68 §3b C1+C2 (Dylan: "we have tents on roads paths. not good.
        // and tents touching tents") — tents get GUARDED placement: ≥120wu
        // from any trail centerline (trail half ~59 + tent half ~60), ≥170wu
        // from each other, and never past the camp's blend rim. A tent with
        // no clean spot after 5 outward nudges is DROPPED — fewer tents
        // beats a tent pitched on the road.
        const _tents: Array<{ x: number; y: number }> = [];
        const T = (fx: number, fy: number, flip?: boolean) => {
          let tx = pk.x + fx * pk.rx, ty = pk.y + fy * pk.ry;
          for (let tries = 0; tries < 5; tries++) {
            const ex = (tx - pk.x) / pk.rx, ey = (ty - pk.y) / pk.ry;
            const inBounds = ex * ex + ey * ey <= 1.15 * 1.15;
            let tentGap = Infinity;
            for (const t0 of _tents) tentGap = Math.min(tentGap, Math.hypot(tx - t0.x, ty - t0.y));
            if (inBounds && _segDistLive(tx, ty) >= 120 && tentGap >= 170) {
              _parkProps.push({ defId: 'tent_military', x: tx, y: ty, flip });
              _tents.push({ x: tx, y: ty });
              return;
            }
            const ang = Math.atan2(ty - pk.y, tx - pk.x) + (prng() - 0.5) * 0.9;
            tx += Math.cos(ang) * 70; ty += Math.sin(ang) * 55;
          }
        };
        T(-0.42 + jit(), -0.30 + jit());
        T(0.40 + jit(), -0.26 + jit(), true);
        if (prng() < 0.6) T(0.02 + jit(), -0.52 + jit());
        P('crate_pallet_pile', 0.30 + jit(), 0.30 + jit());
        P('gas_can', -0.20 + jit(), 0.34 + jit());
        if (prng() < 0.6) P('sandbag_wall', -0.52 + jit(), 0.18 + jit());
        if (prng() < 0.4) P('picnic_table', 0.55 + jit(), 0.10 + jit());
        // 23.59 — camp kit extras
        P('cp_tarp_shelter', 0.52 + jit(), -0.34 + jit());
        P('cp_bedroll', 0.14 + jit(), 0.16 + jit());
        P('cp_lantern', -0.06 + jit(), -0.20 + jit());
      } else if (kind === 'junkyard') {
        // 23.59 — REAL junkyard (pack doc 07): scrap-wall boundary along the
        // back rim, crushed stack as the focal landmark, varied wrecks
        P('jk_scrap_wall_long', -0.30 + jit(), -0.58 + jit());
        P('jk_scrap_wall_long', 0.34 + jit(), -0.56 + jit(), true);
        P('jk_scrap_wall_corner', -0.62 + jit(), -0.38 + jit());
        P('jk_crushed_car_stack', 0.02 + jit(), -0.30 + jit());
        P('jk_shipping_container', 0.48 + jit(), -0.18 + jit());
        const CARS = ['jk_pickup_wreck', 'jk_car_half_buried', 'car_sedan_burnt_left', 'suv_wreck_black', 'taxi_wreck'];
        P(CARS[Math.floor(prng() * CARS.length)], -0.40 + jit(), -0.06 + jit());
        P(CARS[Math.floor(prng() * CARS.length)], 0.26 + jit(), 0.26 + jit(), prng() < 0.5);
        P('jk_forklift_wreck', -0.16 + jit(), 0.34 + jit());
        P('tire_pile_large', 0.52 + jit(), 0.36 + jit());
        P('jk_engine_block', -0.44 + jit(), 0.44 + jit());
        P('jk_floodlight_pole', 0.10 + jit(), -0.02 + jit());
        P('fire_barrel', -0.24 + jit(), 0.14 + jit());
        if (prng() < 0.6) P('jk_metal_sheet_lean', 0.58 + jit(), 0.06 + jit());
        if (prng() < 0.5) P('jk_car_door_loose', 0.34 + jit(), 0.48 + jit());
      } else if (kind === 'neighborhood') {
        // 23.75 N1-B — STREET GRAMMAR (replaces the rejected jittered scatter:
        // Dylan, "looks like shit, random things in the forest"). A real street
        // runs down the pocket's LONG axis; two aligned lot rows FACE it; every
        // placement is deterministic (cellRng by lot index — NO jitter). House
        // lots are ANCHORS only here (real approved-house art + backs land at N2
        // wiring); the dressing uses props already in worldProps.ts. What Dylan
        // gates now is the SHAPE (baked street + [t] lot boxes), not the art.
        const horiz = pk.rx >= pk.ry;                        // street runs along the longer axis
        const halfLen = horiz ? pk.rx : pk.ry;               // half street span
        const rax = horiz ? 0.86 : 0, ray = horiz ? 0 : 0.86;
        const ax0 = pk.x - rax * pk.rx, ay0 = pk.y - ray * pk.ry;   // street ends, just inside the rim
        const ax1 = pk.x + rax * pk.rx, ay1 = pk.y + ray * pk.ry;
        const rlen = Math.hypot(ax1 - ax0, ay1 - ay0) || 1;
        const rnx = -(ay1 - ay0) / rlen, rny = (ax1 - ax0) / rlen;  // street normal (points to a row)
        // lay the street (gentle wobble, same bezier idiom as the spurs above)
        const rsteps = Math.max(3, Math.ceil(rlen / SEG_STEP_WU));
        // 23.76 N1-C — STRAIGHT street (a real road, no wobble)
        let rpx = ax0, rpy = ay0;
        for (let st = 1; st <= rsteps; st++) {
          const t = st / rsteps;
          const qx = ax0 + (ax1 - ax0) * t, qy = ay0 + (ay1 - ay0) * t;
          _pushSeg(rpx, rpy, qx, qy); rpx = qx; rpy = qy;
        }
        // lots: two STRAIGHT rows, pitched for the real house widths (450-900WU)
        // so there's YARD between them, and set well back so front yards read +
        // the two rows never cross-overlap. face points at the street.
        const HOUSE_PITCH = 1150;                            // along-street spacing = house + yard
        const nLots = Math.max(2, Math.floor((halfLen * 2 * 0.9) / HOUSE_PITCH));
        const rowOff = (horiz ? pk.ry : pk.rx) * 0.64;       // set-back from the street centreline (front yards)
        const lots: Lot[] = [];
        for (let side = -1; side <= 1; side += 2) {
          for (let i = 0; i < nLots; i++) {
            const f = (i + 0.5) / nLots;                     // 0..1 down the street
            const cx = ax0 + (ax1 - ax0) * f + rnx * rowOff * side;
            const cy = ay0 + (ay1 - ay0) * f + rny * rowOff * side;
            lots.push({ x: cx, y: cy, hw: 320, hh: 300, face: Math.atan2(-rny * side, -rnx * side) });
            // mailbox at the kerb (between the yard and the street), facing the road
            const mbx = cx - rnx * rowOff * side * 0.7, mby = cy - rny * rowOff * side * 0.7;
            _parkProps.push({ defId: 'mailbox_usps', x: mbx, y: mby, flip: side < 0 });
          }
        }
        // street lamps in an alternating rhythm down the road
        const nLamps = Math.max(2, nLots);
        for (let i = 0; i < nLamps; i++) {
          const f = (i + 0.5) / nLamps, side = (i % 2 === 0) ? 1 : -1;
          const lx = ax0 + (ax1 - ax0) * f + rnx * (horiz ? pk.ry : pk.rx) * 0.22 * side;
          const ly = ay0 + (ay1 - ay0) * f + rny * (horiz ? pk.ry : pk.rx) * 0.22 * side;
          _parkProps.push({ defId: 'street_lamp', x: lx, y: ly });
        }
        // a (bent) stop sign at each street mouth where it meets the entry spur/rim
        _parkProps.push({ defId: 'road_sign_bent', x: ax0 + rnx * 90, y: ay0 + rny * 90 });
        _parkProps.push({ defId: 'road_sign_bent', x: ax1 - rnx * 90, y: ay1 - rny * 90, flip: true });
        // stash the lot anchors on THIS place (pushed at 2c above) for the [t]
        // dev overlay + N2 house wiring
        _places[_places.length - 1].lots = lots;
      } else if (kind === 'crash_site') {
        P('helicopter_wreck', 0 + jit() * 0.5, -0.06 + jit() * 0.5);
        P('rubble_chunks', -0.38 + jit(), 0.24 + jit());
        P('rubble_chunks', 0.40 + jit(), -0.30 + jit());
        P('junk_pile_scrap', 0.44 + jit(), 0.26 + jit());
        P('fire_barrel', -0.28 + jit(), -0.32 + jit());
        P('fire_barrel', 0.24 + jit(), 0.44 + jit());
        P('crate_pallet_pile', -0.50 + jit(), -0.06 + jit());
        if (prng() < 0.6) P('sandbag_wall', 0.56 + jit(), 0.02 + jit());
      } else if (kind === 'grave_patch') {
        const GRAVES = ['gravestone_round', 'gravestone_slab', 'gravestone_cross'];
        for (let gi = 0; gi < 6; gi++) {
          if (prng() < 0.15) continue;
          P(GRAVES[Math.floor(prng() * GRAVES.length)], -0.36 + (gi % 3) * 0.34 + jit(), -0.22 + Math.floor(gi / 3) * 0.42 + jit());
        }
        P('dead_tree', -0.55 + jit(), -0.40 + jit());
        if (prng() < 0.35) P('mausoleum_small', 0.42 + jit(), -0.46 + jit());
        if (prng() < 0.5) P('iron_fence_segment', -0.30 + jit(), 0.52 + jit());
        if (prng() < 0.5) P('iron_fence_segment', 0.34 + jit(), 0.52 + jit(), true);
      } else if (kind === 'nest') {
        P('surge_nest_mound', jit() * 0.4, jit() * 0.4);
        P('rubble_mound_large', -0.40 + jit(), 0.22 + jit());
        P('dead_bush', 0.38 + jit(), -0.28 + jit());
        P('dead_bush', -0.26 + jit(), -0.40 + jit());
        P('dead_tree', 0.50 + jit(), 0.26 + jit());
        if (prng() < 0.5) P('barbed_wire_coil', 0.12 + jit(), 0.46 + jit());
      } else if (kind === 'arena') {
        // combat room: open center, cover on a mid ring (pack doc 03A)
        P('stone_wall_straight', -0.44 + jit(), -0.10 + jit());
        P('stone_wall_corner', 0.42 + jit(), 0.24 + jit());
        P('rubble_mound_large', 0.10 + jit(), -0.44 + jit());
        P('jersey_barrier', -0.24 + jit(), 0.38 + jit());
        P('water_barrier', 0.48 + jit(), -0.30 + jit());
        P('fire_barrel', -0.05 + jit(), 0.05 + jit());
        if (prng() < 0.5) P('broken_column', -0.52 + jit(), 0.30 + jit());
      } else if (kind === 'checkpoint') {
        P(prng() < 0.5 ? 'police_car_lightbar' : 'humvee_wreck', 0.05 + jit(), -0.08 + jit());
        P('wood_barricade', -0.32 + jit(), 0.10 + jit());
        P('wood_barricade', 0.34 + jit(), 0.16 + jit(), true);
        P('traffic_cone', -0.12 + jit(), 0.30 + jit());
        P('traffic_cone_tipped', 0.16 + jit(), 0.34 + jit());
        P('sandbag_wall', -0.44 + jit(), -0.26 + jit());
        P('street_lamp', 0.46 + jit(), -0.30 + jit());
      }
    }
  }

  // 2d. 23.58 — WILD CARDS STATIONS (pack doc 09: "a machine should feel
  // like a destination, not randomly sitting on a dirt trail"). Every
  // machine POI gets station dressing chosen by CONTEXT: token booth in a
  // junkyard, kiosk at a camp, roadside stop elsewhere. The SOUTH approach
  // (the interaction side) stays clear; props sit behind and beside.
  for (const poi of getGroundPois()) {
    if (poi.kind !== 'machine') continue;
    const srng = cellRng(Math.round(poi.x / 64), Math.round(poi.y / 64), seed, 8484);
    let host: Place | null = null;
    for (const pl of _places) {
      const ex = (poi.x - pl.x) / pl.rx, ey = (poi.y - pl.y) / pl.ry;
      if (ex * ex + ey * ey < 1) { host = pl; break; }
    }
    const SP = (defId: string, ox: number, oy: number, flip?: boolean) =>
      _parkProps.push({ defId, x: poi.x + ox + (srng() - 0.5) * 18, y: poi.y + oy + (srng() - 0.5) * 12, flip });
    if (host && host.kind === 'junkyard') {
      // token booth: scrap + fire light
      _stations.push({ x: poi.x, y: poi.y, kind: 'booth' });
      SP('fire_barrel', -150, -40);
      SP('junk_pile_scrap', 150, -90);
      SP('oil_drum_pair', 10, -170);
      SP('tire_single', 100, -20, true);
    } else if (host && host.kind === 'camp') {
      // camp kiosk: supplies
      _stations.push({ x: poi.x, y: poi.y, kind: 'kiosk' });
      SP('crate_pallet_pile', -140, -70);
      SP('gas_can', 115, -40);
      SP('sandbag_wall', 0, -170);
    } else {
      // roadside stop — THINNED 23.68 (Dylan's live review §3b S1-S4: "too
      // much going on... doesn't look good"). The machine is the star: one
      // lamp so it reads at night + one trash can for life. OUT: st_awning
      // (pixel fringe between its poles), st_sign_cards (ace sign — Dylan
      // rejected it), st_token_crate (floated at a bad angle), barricade,
      // cable run. Ground is the organic gravel pad (paintStationPad).
      _stations.push({ x: poi.x, y: poi.y, kind: 'roadside' });
      SP('street_lamp', -155, -35, false);
      SP('trash_can_metal', 95, -125);
    }
  }

  // 23.57 — one-line build summary (a silent partial network was the class
  // of bug behind 'there is no path' — keep the numbers visible)
  try {
    console.log('[paths] build:', JSON.stringify({
      parks: _parks.length, segs: _segs.length, places: _places.length,
      pois: getGroundPois().length, spurAnchors: _spurAnchors.length,
    }));
  } catch { /* noop */ }

  // 3. furnish each site by KIND (seeded; sites own their ground)
  for (const park of _parks) {
    const rng = cellRng(Math.round(park.x / 64), Math.round(park.y / 64), seed, 77);
    if (park.kind === 'graveyard') {
      // 23.34 — GRAVEYARD: mausoleum (or Surge Statue — spookier) presiding
      // over jittered rows of graves, dead trees on the rim.
      _parkProps.push({ defId: rng() < 0.7 ? 'mausoleum_small' : 'statue_surge', x: park.x, y: park.y - PARK_PLAZA_R * 0.32 });
      const rows = 2 + Math.floor(rng() * 2);
      const cols = 3 + Math.floor(rng() * 2);
      const GRAVES = ['gravestone_round', 'gravestone_slab', 'gravestone_cross'];
      for (let ri = 0; ri < rows; ri++) {
        for (let ci = 0; ci < cols; ci++) {
          if (rng() < 0.18) continue;
          _parkProps.push({
            defId: GRAVES[Math.floor(rng() * GRAVES.length)],
            x: park.x + (ci - (cols - 1) / 2) * 96 + (rng() - 0.5) * 22,
            y: park.y + PARK_PLAZA_R * 0.1 + ri * 62 + (rng() - 0.5) * 12,
          });
        }
      }
      _parkProps.push({ defId: 'dead_tree', x: park.x - PARK_PLAZA_R * 0.85, y: park.y - PARK_PLAZA_R * 0.1 });
      if (rng() < 0.6) _parkProps.push({ defId: 'dead_bush', x: park.x + PARK_PLAZA_R * 0.8, y: park.y + PARK_PLAZA_R * 0.2 });
      // 23.38 — rusted iron cemetery fencing flanks the grave rows (east
      // segment flipped so both lean in toward the graves; shadow law holds)
      _parkProps.push({ defId: 'iron_fence_segment', x: park.x - PARK_PLAZA_R * 0.72, y: park.y + PARK_PLAZA_R * 0.42 });
      _parkProps.push({ defId: 'iron_fence_segment', x: park.x + PARK_PLAZA_R * 0.72, y: park.y + PARK_PLAZA_R * 0.42, flip: true });
      continue;
    }
    if (park.kind === 'town_square') {
      // 23.34 — RUINED TOWN SQUARE: broken fountain center, four benches
      // facing in, corner lamps, litter.
      _parkProps.push({ defId: 'fountain_broken', x: park.x, y: park.y + 6 });
      const bench = 'park_bench_wood';
      _parkProps.push({ defId: bench, x: park.x - PARK_PLAZA_R * 0.82, y: park.y + 10, flip: true });
      _parkProps.push({ defId: bench, x: park.x + PARK_PLAZA_R * 0.82, y: park.y + 10, flip: false });
      _parkProps.push({ defId: bench, x: park.x - PARK_PLAZA_R * 0.3, y: park.y - PARK_PLAZA_R * 0.42, flip: true });
      _parkProps.push({ defId: bench, x: park.x + PARK_PLAZA_R * 0.3, y: park.y + PARK_PLAZA_R * 0.48, flip: false });
      // 23.39 — corner lamps face IN (same backward-lamp fix as the trails)
      _parkProps.push({ defId: 'street_lamp', x: park.x - PARK_PLAZA_R * 0.7, y: park.y - PARK_PLAZA_R * 0.35, flip: false });
      _parkProps.push({ defId: 'street_lamp', x: park.x + PARK_PLAZA_R * 0.7, y: park.y + PARK_PLAZA_R * 0.38, flip: true });
      _parkProps.push({ defId: 'trash_can_metal', x: park.x + PARK_PLAZA_R * 0.5, y: park.y - PARK_PLAZA_R * 0.3 });
      if (rng() < 0.5) _parkProps.push({ defId: 'broken_column', x: park.x - PARK_PLAZA_R * 0.45, y: park.y + PARK_PLAZA_R * 0.4 });
      continue;
    }
    // PARK — centerpiece: Surge Statue in ~40%, else a REAL stone campfire
    // ring (23.36 batch-6 art; engine gives it the warm flickering light)
    if (rng() < 0.4) _parkProps.push({ defId: 'statue_surge', x: park.x, y: park.y + 20 });
    else _parkProps.push({ defId: 'campfire_ring', x: park.x, y: park.y + 10 });
    // 23.32 - TWO benches flanking the center, FACING IN (bench art faces
    // LEFT, so the west bench flips to face right/inward). Slide removed -
    // it read out of place; a real playground set-piece comes later.
    const bench = rng() < 0.5 ? 'park_bench_wood' : 'bus_stop_bench';
    _parkProps.push({ defId: bench, x: park.x - PARK_PLAZA_R * 0.78, y: park.y + 14, flip: true });
    _parkProps.push({ defId: bench, x: park.x + PARK_PLAZA_R * 0.78, y: park.y + 14, flip: false });
    // 23.36 — half the parks get a picnic table off the north rim
    if (rng() < 0.5) _parkProps.push({ defId: 'picnic_table', x: park.x + PARK_PLAZA_R * 0.34, y: park.y - PARK_PLAZA_R * 0.52 });
    // 23.37 — 40% get the rusty swing set on the north-west rim (the slide's
    // old spot — this one has no grass base, it belongs on the dirt)
    if (rng() < 0.4) _parkProps.push({ defId: 'swing_set_rusty', x: park.x - PARK_PLAZA_R * 0.42, y: park.y - PARK_PLAZA_R * 0.48 });
    const RING = ['dead_tree', 'tree_stump', 'junk_pile_bags', 'cable_spool', 'trash_can_metal'];
    const n = 2 + Math.floor(rng() * 2);
    const a0 = rng() * Math.PI * 2;
    for (let k = 0; k < n; k++) {
      const ang = a0 + (k / n) * Math.PI * 2 + (rng() - 0.5) * 0.5;
      const rr = PARK_PLAZA_R * (0.75 + rng() * 0.2);
      const px2 = park.x + Math.cos(ang) * rr;
      const py2 = park.y + Math.sin(ang) * rr * 0.5;
      if (Math.abs(py2 - (park.y + 14)) < 40 && Math.abs(Math.abs(px2 - park.x) - PARK_PLAZA_R * 0.78) < 60) continue;
      _parkProps.push({ defId: RING[Math.floor(rng() * RING.length)], x: px2, y: py2 });
    }
  }
}

export function getPathLamps(seed: number): PathLamp[] {
  _ensureNetwork(seed);
  return _lamps;
}
export function getParkSites(seed: number): ParkSite[] {
  _ensureNetwork(seed);
  return _parks;
}
export function getParkProps(seed: number): ParkProp[] {
  _ensureNetwork(seed);
  return _parkProps;
}

export function distToNearestPath(x: number, y: number, seed: number): number {
  _ensureNetwork(seed);
  const segs = _buckets.get(`${Math.floor(x / CHUNK_WU)},${Math.floor(y / CHUNK_WU)}`);
  if (!segs || !segs.length) return Infinity;
  let best = Infinity;
  for (const s of segs) {
    const vx = s.x2 - s.x1, vy = s.y2 - s.y1;
    const ll = vx * vx + vy * vy || 1;
    let t = ((x - s.x1) * vx + (y - s.y1) * vy) / ll;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    const dx = x - (s.x1 + vx * t), dy = y - (s.y1 + vy * t);
    const d = dx * dx + dy * dy;
    if (d < best) best = d;
  }
  return Math.sqrt(best);
}

export function distToNearestPark(x: number, y: number, seed: number): number {
  _ensureNetwork(seed);
  // F7 perf (co-op audit): squared-distance scan, ONE sqrt at the end — this
  // runs ~700-1000× per chunk bake inside forestClearance. Exact same result.
  let bestSq = Infinity;
  for (const p of _parks) {
    const dx = x - p.x, dy = y - p.y;
    const dSq = dx * dx + dy * dy;
    if (dSq < bestSq) bestSq = dSq;
  }
  return Math.sqrt(bestSq);
}

// ── 23.61 · GROUND PASS 2: per-kit PATH MATERIALS ("the trail becomes the
// place"): where a trail crosses a PLACE its surface re-strokes to that
// place's material — oily gravel through a junkyard, damp stone through a
// graveyard, broken asphalt entering a neighborhood, ash at a crash site.
// 'compacted' (arena/checkpoint) is intentionally absent — those stay dirt.
// 23.71 §3b R1 (Dylan: "the dark roads. need way more texture and better
// gradient into the dirt path... sticks out like a sore thumb") — the dark
// kits were near-black voids: every body lifted toward a readable surfaced-
// trail tone (identity kept, void gone) and every walked-in CENTER pushed
// visibly brighter than its body so the lane reads at gameplay zoom.
const PATH_MATERIALS: Partial<Record<GroundKitId, { body: string; body2: string; center: string; edge: string }>> = {
  junkyard_floor: { body: '#3a342b', body2: '#453e33', center: '#585044', edge: 'rgba(58,52,43,0.5)' },
  grave_soil:     { body: '#383832', body2: '#42423a', center: '#54544a', edge: 'rgba(56,56,50,0.5)' },
  camp_ground:    { body: '#4a4030', body2: '#544a37', center: '#685c47', edge: 'rgba(74,64,48,0.5)' },
  scorched:       { body: '#2a231b', body2: '#342b21', center: '#453a2c', edge: 'rgba(42,35,27,0.55)' },
  blackened:      { body: '#261c2b', body2: '#302336', center: '#3f2e46', edge: 'rgba(38,28,43,0.55)' },
  yard:           { body: '#403f3b', body2: '#4a4945', center: '#5a5953', edge: 'rgba(64,63,59,0.5)' },
};

/** Bake trails + park plazas into a chunk canvas (mapChunks L4.5). */
export function drawPathsIntoChunk(
  c: CanvasRenderingContext2D,
  chunkX: number, chunkY: number, seed: number,
  lx: (wx: number) => number, ly: (wy: number) => number, pxPerWu: number,
): void {
  _ensureNetwork(seed);
  const chunkWX = chunkX * CHUNK_WU, chunkWY = chunkY * CHUNK_WU;
  const segs = _buckets.get(`${chunkX},${chunkY}`) ?? [];

  const wPx = PATH_WIDTH_WU * pxPerWu;
  const stroke = (offX: number, offY: number, color: string, width: number, dash?: number[]) => {
    if (!segs.length) return;
    c.save();
    c.strokeStyle = color;
    c.lineWidth = width;
    c.lineCap = 'round';
    c.lineJoin = 'round';
    if (dash) c.setLineDash(dash);
    c.beginPath();
    for (const s of segs) {
      c.moveTo(lx(s.x1) + offX, ly(s.y1) + offY);
      c.lineTo(lx(s.x2) + offX, ly(s.y2) + offY);
    }
    c.stroke();
    c.restore();
  };

  // DIRT TRAIL (not a slab): soft NE shading, warm packed-earth body with a
  // lighter walked-in center, ragged dashed edge wear so it bleeds into the
  // lot instead of reading like poured concrete.
  stroke(4, -3, 'rgba(8,6,4,0.22)', wPx + 5);                       // soft NE shade
  stroke(0, 0, '#3d3226', wPx);                                     // packed dirt
  stroke(0, 0, '#463a2b', wPx - 5);                                 // body
  stroke(0, 0, '#4e4130', wPx * 0.45);                              // walked-in center
  stroke(0, 0, 'rgba(61,50,38,0.5)', wPx + 8, [7, 11]);             // ragged edge bleed
  // ── 23.46 · ZONE C: OUTER TRANSITION (Dylan: "no gradual transition from
  // path to forest... leaf litter creeping onto path"). A wide ragged band
  // of leaf-litter and scrub tones straddling the path edge, so the trail
  // dissolves into the woods instead of being stamped on the lot. ──
  if (segs.length) {
    for (const sg of segs) {
      const rngT = cellRng(Math.round(sg.x1 * 0.7), Math.round(sg.y1 * 0.7), seed, 6161);
      const dxs2 = sg.x2 - sg.x1, dys2 = sg.y2 - sg.y1;
      const ll = Math.hypot(dxs2, dys2) || 1;
      const nx = -dys2 / ll, ny = dxs2 / ll;
      const LITTER = ['#33301f', '#3b3524', '#2c2a1c', '#403626'];
      const nBlob = 2 + Math.floor(rngT() * 3);
      for (let b = 0; b < nBlob; b++) {
        const side = rngT() < 0.5 ? 1 : -1;
        const off = side * PATH_WIDTH_WU * (0.42 + rngT() * 0.85);   // straddles the edge
        const along = rngT();
        const bx3 = sg.x1 + dxs2 * along + nx * off;
        const by3 = sg.y1 + dys2 * along + ny * off;
        const rw = (16 + rngT() * 30) * pxPerWu;
        c.save();
        c.globalAlpha = 0.14 + rngT() * 0.16;
        c.fillStyle = LITTER[Math.floor(rngT() * LITTER.length)];
        c.beginPath();
        c.ellipse(lx(bx3), ly(by3), rw, rw * 0.42, (rngT() - 0.5) * 0.6, 0, Math.PI * 2);
        c.fill();
        c.restore();
      }
      // grass clumps + twigs in the transition band
      if (rngT() < 0.5) {
        const side = rngT() < 0.5 ? 1 : -1;
        const off = side * PATH_WIDTH_WU * (0.55 + rngT() * 0.7);
        const gx3 = lx(sg.x1 + dxs2 * 0.5 + nx * off), gy3 = ly(sg.y1 + dys2 * 0.5 + ny * off);
        c.save();
        c.strokeStyle = rngT() < 0.6 ? '#3f4426' : '#4a4a2c';
        c.globalAlpha = 0.55;
        c.lineWidth = 1;
        const blades = 3 + Math.floor(rngT() * 3);
        for (let g2 = 0; g2 < blades; g2++) {
          const a2 = -Math.PI / 2 + (g2 - blades / 2) * 0.28 + (rngT() - 0.5) * 0.2;
          const ln = (4 + rngT() * 5) * pxPerWu;
          c.beginPath();
          c.moveTo(gx3, gy3);
          c.lineTo(gx3 + Math.cos(a2) * ln, gy3 + Math.sin(a2) * ln * 0.8);
          c.stroke();
        }
        c.restore();
      }
      if (rngT() < 0.3) {   // fallen twig
        const side = rngT() < 0.5 ? 1 : -1;
        const off = side * PATH_WIDTH_WU * (0.35 + rngT() * 0.8);
        const tx3 = lx(sg.x1 + dxs2 * rngT() + nx * off), ty3 = ly(sg.y1 + dys2 * 0.5 + ny * off);
        c.save();
        c.strokeStyle = '#241d12';
        c.globalAlpha = 0.7;
        c.lineWidth = 1.4;
        const a2 = rngT() * Math.PI;
        const ln = (7 + rngT() * 9) * pxPerWu;
        c.beginPath();
        c.moveTo(tx3 - Math.cos(a2) * ln / 2, ty3 - Math.sin(a2) * ln * 0.2);
        c.lineTo(tx3 + Math.cos(a2) * ln / 2, ty3 + Math.sin(a2) * ln * 0.2);
        c.stroke();
        c.restore();
      }
    }
    // ── ZONE B: BROKEN INNER EDGE — ground-tone scallops BITE INTO the
    // path so the edge is never a clean stroke line ──
    for (const sg of segs) {
      const rngE = cellRng(Math.round(sg.x2 * 0.9), Math.round(sg.y2 * 0.9), seed, 7272);
      const dxs2 = sg.x2 - sg.x1, dys2 = sg.y2 - sg.y1;
      const ll = Math.hypot(dxs2, dys2) || 1;
      const nx = -dys2 / ll, ny = dxs2 / ll;
      const nBite = 1 + Math.floor(rngE() * 3);
      for (let b = 0; b < nBite; b++) {
        const side = rngE() < 0.5 ? 1 : -1;
        const off = side * PATH_WIDTH_WU * (0.46 + rngE() * 0.1);
        const along = rngE();
        const bx3 = sg.x1 + dxs2 * along + nx * off;
        const by3 = sg.y1 + dys2 * along + ny * off;
        const rw = (7 + rngE() * 13) * pxPerWu;
        c.save();
        c.globalAlpha = 0.5 + rngE() * 0.3;
        c.fillStyle = rngE() < 0.5 ? '#2e2820' : '#332c22';
        c.beginPath();
        c.ellipse(lx(bx3), ly(by3), rw, rw * 0.5, (rngE() - 0.5) * 0.8, 0, Math.PI * 2);
        c.fill();
        c.restore();
      }
      // ZONE A keeps readable: pebbles live OFF-CENTER (the worn lane stays clean)
      const nPeb = 4 + Math.floor(rngE() * 7);
      c.save();
      for (let k2 = 0; k2 < nPeb; k2++) {
        const side = rngE() < 0.5 ? 1 : -1;
        const off = side * PATH_WIDTH_WU * (0.16 + rngE() * 0.32);
        const along = rngE();
        const sxp = lx(sg.x1 + dxs2 * along + nx * off);
        const syp = ly(sg.y1 + dys2 * along + ny * off);
        const g2 = 96 + Math.floor(rngE() * 44);
        c.fillStyle = `rgba(${g2},${g2 - 8},${g2 - 20},${0.35 + rngE() * 0.3})`;
        const pw = 1 + rngE() * 1.8;
        c.fillRect(sxp, syp, pw, pw * 0.7);
      }
      c.restore();
    }
  }

  // 23.43 — WIDTH BREATHING: a second body pass over ~40% of segments at
  // +30% width makes the road widen and narrow like real walked dirt
  // (Dylan vs mockups: "widens and such").
  if (segs.length) {
    c.save();
    c.strokeStyle = '#42372a';
    c.lineCap = 'round';
    for (const sg of segs) {
      const hh = _h2(sg.x1, sg.y1);
      if ((hh & 7) > 2) continue;
      c.lineWidth = wPx * (1.14 + ((hh >>> 4) & 15) / 15 * 0.28);
      c.beginPath();
      c.moveTo(lx(sg.x1), ly(sg.y1));
      c.lineTo(lx(sg.x2), ly(sg.y2));
      c.stroke();
    }
    c.restore();
    // 23.43 — TEXTURE MIX: dust patches, wet spots, stone speckles seeded
    // along every segment ("textures are mixing in it" vs our flat fill)
    for (const sg of segs) {
      const rng2 = cellRng(Math.round(sg.x1), Math.round(sg.y1), seed, 3711);
      const mx = (sg.x1 + sg.x2) / 2, my = (sg.y1 + sg.y2) / 2;
      const dxs2 = sg.x2 - sg.x1, dys2 = sg.y2 - sg.y1;
      const ll = Math.hypot(dxs2, dys2) || 1;
      const nx = -dys2 / ll, ny = dxs2 / ll;
      if (rng2() < 0.42) {   // dust patch (lighter)
        const off = (rng2() - 0.5) * PATH_WIDTH_WU * 0.7;
        const rw = (12 + rng2() * 22) * pxPerWu;
        c.save();
        c.globalAlpha = 0.12 + rng2() * 0.12;
        c.fillStyle = '#5c4f3a';
        c.beginPath();
        c.ellipse(lx(mx + nx * off), ly(my + ny * off), rw, rw * 0.45, 0, 0, Math.PI * 2);
        c.fill();
        c.restore();
      }
      if (rng2() < 0.26) {   // wet/dark patch
        const off = (rng2() - 0.5) * PATH_WIDTH_WU * 0.8;
        const rw = (10 + rng2() * 18) * pxPerWu;
        c.save();
        c.globalAlpha = 0.14 + rng2() * 0.1;
        c.fillStyle = '#241d15';
        c.beginPath();
        c.ellipse(lx(mx + nx * off), ly(my + ny * off), rw, rw * 0.4, 0, 0, Math.PI * 2);
        c.fill();
        c.restore();
      }
      if (rng2() < 0.3) {    // stones
        c.save();
        c.fillStyle = 'rgba(122,112,98,0.55)';
        const n2 = 1 + Math.floor(rng2() * 3);
        for (let k = 0; k < n2; k++) {
          const off = (rng2() - 0.5) * PATH_WIDTH_WU * 0.9;
          const along = rng2();
          const sxp = lx(sg.x1 + dxs2 * along + nx * off);
          const syp = ly(sg.y1 + dys2 * along + ny * off);
          c.fillRect(sxp, syp, 1 + rng2() * 1.6, 1 + rng2());
        }
        c.restore();
      }
    }
  }
  // 23.32 - dashed center highlight DELETED (Dylan: the yellow dashes in
  // the middle of the dirt road were OURS, not lot paint)

  // park plazas that touch this chunk: worn dirt clearing (ground ellipse)
  for (const p of _parks) {
    if (p.x < chunkWX - 500 || p.x > chunkWX + CHUNK_WU + 500 ||
        p.y < chunkWY - 500 || p.y > chunkWY + CHUNK_WU + 500) continue;
    const rx = PARK_PLAZA_R * pxPerWu;
    const ry = rx * 0.5;   // ground ellipse
    const cxp = lx(p.x), cyp = ly(p.y);
    const g = c.createRadialGradient(cxp, cyp, rx * 0.15, cxp, cyp, rx);
    // 23.34 — ground varies by kind: warm dirt (park), cold grave earth
    // (graveyard), pale cracked pavers (town square)
    if (p.kind === 'graveyard') {
      g.addColorStop(0, 'rgba(66,60,54,0.95)');
      g.addColorStop(0.72, 'rgba(52,48,44,0.88)');
      g.addColorStop(1, 'rgba(52,48,44,0)');
    } else if (p.kind === 'town_square') {
      g.addColorStop(0, 'rgba(108,102,92,0.95)');
      g.addColorStop(0.72, 'rgba(86,81,72,0.88)');
      g.addColorStop(1, 'rgba(86,81,72,0)');
    } else {
      g.addColorStop(0, 'rgba(96,81,60,0.95)');
      g.addColorStop(0.72, 'rgba(74,62,46,0.88)');
      g.addColorStop(1, 'rgba(74,62,46,0)');
    }
    c.save();
    c.translate(cxp, cyp);
    c.scale(1, ry / rx);
    c.translate(-cxp, -cyp);
    c.fillStyle = g;
    c.fillRect(cxp - rx, cyp - rx, rx * 2, rx * 2);
    c.restore();
    // seeded wear inside the plaza: scuffs + a few stones
    const rng = cellRng(Math.round(p.x / 64), Math.round(p.y / 64), seed, 78);
    for (let i = 0; i < 10; i++) {
      const ang = rng() * Math.PI * 2, rr = rng() * rx * 0.8;
      const sx = cxp + Math.cos(ang) * rr, sy = cyp + Math.sin(ang) * rr * 0.5;
      c.fillStyle = rng() < 0.5 ? 'rgba(30,25,19,0.25)' : 'rgba(120,104,82,0.15)';
      c.beginPath();
      c.ellipse(sx, sy, 2 + rng() * 6, (1 + rng() * 3), 0, 0, Math.PI * 2);
      c.fill();
    }
  }

  // ── 23.61 · GROUND PASS 2: PATH MATERIAL SHIFT — where the trail crosses a
  // PLACE, re-stroke its surface in that place's material. Two clipped passes
  // (full ellipse at half strength, inner ellipse full) so the material FADES
  // in across the rim band instead of switching at a hard line. Per-kit
  // surface decals (gravel/tire ruts, stepping stones, asphalt patches…)
  // are seeded per SAMPLE POINT so both sides of a chunk border agree. ──
  if (segs.length) {
    for (const pl of _places) {
      if (pl.x + pl.rx < chunkWX - 60 || pl.x - pl.rx > chunkWX + CHUNK_WU + 60 ||
          pl.y + pl.ry < chunkWY - 60 || pl.y - pl.ry > chunkWY + CHUNK_WU + 60) continue;
      const mat = PATH_MATERIALS[pl.groundKit];
      if (!mat) continue;
      const pxc = lx(pl.x), pyc = ly(pl.y);
      const rxp = pl.rx * pxPerWu, ryp = pl.ry * pxPerWu;
      // 23.71 R1 — four shrinking passes instead of two: the material now
      // RAMPS in over the rim band (0.28→1.0) as the trail enters the place,
      // so the dirt path gradually becomes the place's surface instead of
      // stepping to it ("better gradient into the dirt path").
      for (const pass of [{ k: 1.0, a: 0.28 }, { k: 0.9, a: 0.45 }, { k: 0.79, a: 0.72 }, { k: 0.68, a: 1.0 }]) {
        c.save();
        c.beginPath();
        c.ellipse(pxc, pyc, rxp * pass.k, ryp * pass.k, 0, 0, Math.PI * 2);
        c.clip();
        c.globalAlpha = pass.a;
        stroke(0, 0, mat.body, wPx);
        stroke(0, 0, mat.body2, wPx - 5);
        stroke(0, 0, mat.center, wPx * 0.45);
        stroke(0, 0, mat.edge, wPx + 8, [7, 11]);
        c.restore();
      }
      // surface decals along the trail inside the place
      for (const sg of segs) {
        const dxs = sg.x2 - sg.x1, dys = sg.y2 - sg.y1;
        const len = Math.hypot(dxs, dys) || 1;
        const dirx = dxs / len, diry = dys / len;
        const nxp = -diry, nyp = dirx;
        for (let t = 17; t < len; t += 34) {
          const wx = sg.x1 + dirx * t, wy = sg.y1 + diry * t;
          const ex = (wx - pl.x) / pl.rx, ey = (wy - pl.y) / pl.ry;
          if (ex * ex + ey * ey > 0.85) continue;   // stay inside the material zone
          const rngS = cellRng(Math.round(wx), Math.round(wy), seed, 5151);
          const kit = pl.groundKit;
          // 23.71 R1 — WAY MORE TEXTURE (all kits): broken coverage. Worn
          // patches let the warm dirt trail show through the material, and
          // tonal mottle breaks up the flat body — the surface reads as a
          // used trail wearing the place's material, never a dark smear.
          for (let m = 0; m < 2; m++) {
            if (rngS() < 0.62) {
              const off = (rngS() - 0.5) * PATH_WIDTH_WU * 0.75;
              c.save();
              c.globalAlpha = 0.09 + rngS() * 0.12;
              c.fillStyle = rngS() < 0.45 ? '#463a2b' : mat.center;
              const mr = (6 + rngS() * 12) * pxPerWu;
              c.beginPath();
              c.ellipse(lx(wx + nxp * off), ly(wy + nyp * off), mr, mr * 0.45, rngS() * Math.PI, 0, Math.PI * 2);
              c.fill();
              c.restore();
            }
          }
          if (kit === 'junkyard_floor') {
            // gravel + metal chips + oil dabs + intermittent tire RUTS
            for (let i = 0; i < 3; i++) {
              const off = (rngS() - 0.5) * PATH_WIDTH_WU * 0.8;
              c.fillStyle = `rgba(124,116,102,${0.10 + rngS() * 0.12})`;
              c.fillRect(lx(wx + nxp * off), ly(wy + nyp * off), 1 + rngS() * 1.8, 1 + rngS() * 1.2);
            }
            if (rngS() < 0.22) {
              const off = (rngS() - 0.5) * PATH_WIDTH_WU * 0.6;
              c.save();
              c.globalAlpha = 0.16 + rngS() * 0.1;
              c.fillStyle = '#0a0806';
              c.beginPath();
              const orp = (5 + rngS() * 9) * pxPerWu;
              c.ellipse(lx(wx + nxp * off), ly(wy + nyp * off), orp, orp * 0.5, rngS() * Math.PI, 0, Math.PI * 2);
              c.fill();
              c.restore();
            }
            if (rngS() < 0.45) {
              c.save();
              c.strokeStyle = `rgba(18,15,11,${0.14 + rngS() * 0.12})`;
              c.lineWidth = 2.1;
              const hl = (13 + rngS() * 9) * pxPerWu;
              for (const so of [-3.6, 3.6]) {
                const bx = lx(wx + nxp * so), by = ly(wy + nyp * so);
                c.beginPath();
                c.moveTo(bx - dirx * hl, by - diry * hl * 0.55);
                c.lineTo(bx + dirx * hl, by + diry * hl * 0.55);
                c.stroke();
              }
              c.restore();
            }
          } else if (kit === 'grave_soil') {
            // stepping stones down the walked lane + moss creep
            if (rngS() < 0.55) {
              const off = (rngS() - 0.5) * PATH_WIDTH_WU * 0.3;
              c.save();
              c.globalAlpha = 0.24 + rngS() * 0.12;
              c.fillStyle = '#6c6a64';
              const sr = (6 + rngS() * 6) * pxPerWu;
              c.beginPath();
              c.ellipse(lx(wx + nxp * off), ly(wy + nyp * off), sr, sr * 0.55, rngS() * Math.PI, 0, Math.PI * 2);
              c.fill();
              c.restore();
            }
            if (rngS() < 0.25) {
              const off = (rngS() < 0.5 ? -1 : 1) * PATH_WIDTH_WU * (0.3 + rngS() * 0.25);
              c.save();
              c.globalAlpha = 0.12 + rngS() * 0.08;
              c.fillStyle = '#2e4022';
              const mr = (4 + rngS() * 7) * pxPerWu;
              c.beginPath();
              c.ellipse(lx(wx + nxp * off), ly(wy + nyp * off), mr, mr * 0.5, 0, 0, Math.PI * 2);
              c.fill();
              c.restore();
            }
          } else if (kit === 'camp_ground') {
            // straw flecks + trampled scuffs
            for (let i = 0; i < 2; i++) {
              const off = (rngS() - 0.5) * PATH_WIDTH_WU * 0.7;
              c.fillStyle = `rgba(150,132,80,${0.10 + rngS() * 0.10})`;
              c.fillRect(lx(wx + nxp * off), ly(wy + nyp * off), 1 + rngS() * 2, 1);
            }
            if (rngS() < 0.3) {
              const off = (rngS() - 0.5) * PATH_WIDTH_WU * 0.5;
              c.save();
              c.globalAlpha = 0.1 + rngS() * 0.08;
              c.fillStyle = '#322c26';
              const tr = (5 + rngS() * 7) * pxPerWu;
              c.beginPath();
              c.ellipse(lx(wx + nxp * off), ly(wy + nyp * off), tr, tr * 0.4, rngS() * Math.PI, 0, Math.PI * 2);
              c.fill();
              c.restore();
            }
          } else if (kit === 'scorched') {
            // soot blooms + pale ash flecks
            if (rngS() < 0.4) {
              const off = (rngS() - 0.5) * PATH_WIDTH_WU * 0.7;
              c.save();
              c.globalAlpha = 0.16 + rngS() * 0.12;
              c.fillStyle = '#060504';
              const sr = (6 + rngS() * 10) * pxPerWu;
              c.beginPath();
              c.ellipse(lx(wx + nxp * off), ly(wy + nyp * off), sr, sr * 0.5, rngS() * Math.PI, 0, Math.PI * 2);
              c.fill();
              c.restore();
            }
            for (let i = 0; i < 2; i++) {
              const off = (rngS() - 0.5) * PATH_WIDTH_WU * 0.8;
              c.fillStyle = `rgba(120,112,100,${0.08 + rngS() * 0.08})`;
              c.fillRect(lx(wx + nxp * off), ly(wy + nyp * off), 1 + rngS() * 1.5, 1);
            }
          } else if (kit === 'blackened') {
            // crystal dust glinting in the corrupt ground
            for (let i = 0; i < 2; i++) {
              const off = (rngS() - 0.5) * PATH_WIDTH_WU * 0.7;
              c.fillStyle = `rgba(190,120,240,${0.08 + rngS() * 0.10})`;
              c.fillRect(lx(wx + nxp * off), ly(wy + nyp * off), 1 + rngS() * 1.4, 1);
            }
          } else if (kit === 'yard') {
            // broken asphalt patches + potholes + hairline cracks
            if (rngS() < 0.4) {
              const off = (rngS() - 0.5) * PATH_WIDTH_WU * 0.6;
              c.save();
              c.globalAlpha = 0.2 + rngS() * 0.12;
              c.fillStyle = rngS() < 0.5 ? '#4a4946' : '#302f2d';
              c.translate(lx(wx + nxp * off), ly(wy + nyp * off));
              c.rotate((rngS() - 0.5) * 0.5);
              const aw = (8 + rngS() * 10) * pxPerWu, ah = aw * (0.4 + rngS() * 0.3);
              c.fillRect(-aw / 2, -ah / 2, aw, ah);
              c.restore();
            }
            if (rngS() < 0.2) {
              c.save();
              c.strokeStyle = 'rgba(24,23,21,0.4)';
              c.lineWidth = 1.2;
              let ax2 = lx(wx), ay2 = ly(wy), ang2 = rngS() * Math.PI * 2;
              c.beginPath();
              c.moveTo(ax2, ay2);
              for (let s2 = 0; s2 < 4; s2++) {
                ang2 += (rngS() - 0.5) * 1.4;
                ax2 += Math.cos(ang2) * 5 * pxPerWu;
                ay2 += Math.sin(ang2) * 5 * pxPerWu * 0.55;
                c.lineTo(ax2, ay2);
              }
              c.stroke();
              c.restore();
            }
          }
        }
      }
    }
  }
}

/** 23.41 — POIs register AFTER the first network build on a fresh floor;
 *  mapChunks calls this from setGroundPois so spurs rebuild with them. */
/** 23.57 — places (purposeful pocket nodes) for debug labels + validation. */
export function getPlaces(seed: number): Place[] {
  _ensureNetwork(seed);
  return _places;
}

/** 23.61 — machine stations (kind decided once, shared by props + ground). */
export function getMachineStations(seed: number): MachineStation[] {
  _ensureNetwork(seed);
  return _stations;
}

/** 23.56 — engine registers spawn/portal/extraction so they get spur trails. */
export function setSpurAnchors(anchors: Array<{ x: number; y: number }>): void {
  _spurAnchors = anchors;
  invalidatePathNetwork();
}
/** One-off additions (extraction port spawns mid-run). */
export function addSpurAnchor(a: { x: number; y: number }): void {
  _spurAnchors.push(a);
  invalidatePathNetwork();
}

export function invalidatePathNetwork(): void {
  _builtSeed = null;
}
